/**
 * /jobs/categories/new and /jobs/categories/:categoryId — create/edit a job category.
 * Legacy parity: title (required), description (required).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Skeleton,
  Textarea,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import {
  useJobCategoryDetail,
  useCreateJobCategory,
  useUpdateJobCategory,
  type JobCategoryCreatePayload,
} from '@/api/jobs';

/* ── Component ──────────────────────────────────────────────────────────── */

export default function JobCategoryFormPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const isEdit = !!categoryId;
  const id = Number(categoryId);
  const navigate = useNavigate();
  const toast = useToast();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitError, setSubmitError] = useState('');

  /* ── Data ─────────────────────────────────────────────────────────────── */
  const detailQ = useJobCategoryDetail(isEdit ? id : 0);

  useEffect(() => {
    const cat = detailQ.data?.result;
    if (!cat) return;
    setTitle(cat.title ?? '');
    setDescription(cat.description ?? '');
  }, [detailQ.data]);

  /* ── Mutations ─────────────────────────────────────────────────────────── */
  const createMut = useCreateJobCategory();
  const updateMut = useUpdateJobCategory(id);
  const isPending = createMut.isPending || updateMut.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (!title.trim()) {
      setSubmitError('Title is required.');
      return;
    }
    if (!description.trim()) {
      setSubmitError('Description is required.');
      return;
    }

    const payload: JobCategoryCreatePayload = {
      title: title.trim(),
      description: description.trim(),
    };

    try {
      if (isEdit) {
        await updateMut.mutateAsync(payload);
        toast.success('Category updated successfully.');
      } else {
        await createMut.mutateAsync(payload);
        toast.success('Category created successfully.');
        navigate('/jobs/categories');
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string; message?: string } } })
          ?.response?.data?.error ??
        (err as { response?: { data?: { message?: string } } })
          ?.response?.data?.message ??
        'An unexpected error occurred.';
      setSubmitError(msg);
    }
  };

  /* ── Loading / error (edit mode) ──────────────────────────────────────── */
  if (isEdit && detailQ.isLoading) {
    return (
      <Container size="lg">
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 40 }} />
          ))}
        </div>
      </Container>
    );
  }

  if (isEdit && detailQ.isError) {
    return (
      <Container size="lg">
        <Alert
          tone="danger"
          title="Failed to load category"
          actions={
            <Button size="sm" variant="ghost" onClick={() => detailQ.refetch()}>
              Retry
            </Button>
          }
        />
      </Container>
    );
  }

  /* ── Render ─────────────────────────────────────────────────────────────── */
  return (
    <Container size="lg">
      <PageHeader
        title={isEdit ? 'Edit Category' : 'New Category'}
        description={
          isEdit
            ? `Editing category #${id}`
            : 'Create a new job category to organise postings.'
        }
        style={{ marginBottom: 'var(--vn-space-4)' }}
        actions={
          <Button
            variant="secondary"
            startIcon={<ArrowLeft size={15} />}
            onClick={() => navigate('/jobs/categories')}
          >
            Back to Categories
          </Button>
        }
      />

      <form onSubmit={handleSubmit} noValidate>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Card>
            <CardHeader>
              <CardTitle>Category Information</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                <Field label="Title" required>
                  {(p) => (
                    <Input
                      {...p}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g. Development"
                    />
                  )}
                </Field>
                <Field label="Description" required>
                  {(p) => (
                    <Textarea
                      {...p}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe what this category covers…"
                      rows={4}
                    />
                  )}
                </Field>
              </div>
            </CardBody>
          </Card>

          {submitError && (
            <Alert tone="danger" title="Submission failed">
              {submitError}
            </Alert>
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--vn-space-2)',
            }}
          >
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate('/jobs/categories')}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              startIcon={<Save size={15} />}
              disabled={isPending}
            >
              {isPending
                ? isEdit
                  ? 'Saving…'
                  : 'Creating…'
                : isEdit
                  ? 'Save changes'
                  : 'Create category'}
            </Button>
          </div>
        </div>
      </form>
    </Container>
  );
}
